import React from 'react';
import { Input, InputNumber, Modal, Radio, message } from 'antd';
import { EBillingStatus, EChargeType, TPaymentBilling } from 'types/provider';
import { createPracticeUnifiedRefund, createUnifiedRefund } from 'api/provider';
import { REQUIRED_FIELD } from 'constants/common';
import s from './s.module.less';

// PARTIAL_REFUNDED: an invoice may still hold payments that were never refunded; the backend's
// refundableAmountCents already excludes refunded / in-flight payments, so it decides what's left
const REFUNDABLE_STATUSES = [
    EBillingStatus.SUCCEEDED,
    EBillingStatus.FULLY_PAID,
    EBillingStatus.PARTIAL_PAID,
    EBillingStatus.PARTIAL_REFUNDED,
];

const REASON_OPTIONS = [
    { value: 'Duplicate charge', label: 'Duplicate charge' },
    { value: 'Billing error', label: 'Billing error' },
    { value: 'Courtesy refund', label: 'Courtesy refund' },
    { value: 'Patient canceled', label: 'Patient canceled' },
    { value: 'Others', label: 'Others' },
];

export const canIssueRefund = (item: TPaymentBilling) =>
    (item.refundableAmountCents || 0) > 0 &&
    item.chargeType !== EChargeType.TEXT_VISIT &&
    REFUNDABLE_STATUSES.includes(item.billingStatus);

interface Props {
    open: boolean;
    item?: TPaymentBilling;
    // Practice View refunds are validated against the org scope by a separate endpoint
    isGroupPractice?: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const RefundModal = ({ open, item, isGroupPractice, onCancel, onSuccess }: Props) => {
    const [selectFullRefund, setSelectFullRefund] = React.useState(false);
    const [refundAmount, setRefundAmount] = React.useState<number | null>(null);
    const [selectReason, setSelectReason] = React.useState<string>();
    const [reasonOthers, setReasonOthers] = React.useState<string>();
    const [numberError, setNumberError] = React.useState<string>();
    const [showReasonError, setShowReasonError] = React.useState(false);
    const [refunding, setRefunding] = React.useState(false);

    const maxRefundAmount = (item?.refundableAmountCents || 0) / 100;

    React.useEffect(() => {
        if (open) {
            setSelectFullRefund(false);
            setRefundAmount(null);
            setSelectReason(undefined);
            setReasonOthers(undefined);
            setNumberError(undefined);
            setShowReasonError(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        if (!item) {
            return;
        }
        let canSubmit = true;
        // Reason is optional (10-13 sync); only "Others" needs its free-text explanation
        if (selectReason === 'Others' && !reasonOthers?.trim()) {
            setShowReasonError(true);
            canSubmit = false;
        }
        if (!selectFullRefund) {
            if (!refundAmount || refundAmount <= 0) {
                setNumberError(REQUIRED_FIELD);
                canSubmit = false;
            } else if (refundAmount > maxRefundAmount) {
                setNumberError(`Refund amount cannot exceed $${maxRefundAmount.toFixed(2)}`);
                canSubmit = false;
            }
        }
        if (!canSubmit) {
            return;
        }
        const refund = isGroupPractice ? createPracticeUnifiedRefund : createUnifiedRefund;
        setRefunding(true);
        const res = await refund({
            billingFrom: item.billingFrom,
            billingId: item.id,
            refundAmount: selectFullRefund ? maxRefundAmount : refundAmount || 0,
            reason: (selectReason === 'Others' ? reasonOthers?.trim() : selectReason) || '',
        });
        setRefunding(false);
        if (!res.error) {
            message.success('Refund created successfully');
            onSuccess();
        }
    };

    return (
        <Modal
            title="Issue a refund"
            open={open}
            width={600}
            onCancel={onCancel}
            wrapClassName={s.refundModalWrap}
            okText="Confirm"
            onOk={handleConfirm}
            okButtonProps={{
                loading: refunding,
            }}
        >
            <div className={s.refundWrap}>
                <div className={s.reItem} onClick={() => setSelectFullRefund(false)}>
                    <Radio checked={!selectFullRefund}>Partial refund</Radio>
                    <div className={s.right}>
                        <InputNumber
                            value={refundAmount}
                            status={numberError ? 'error' : undefined}
                            onFocus={() => setSelectFullRefund(false)}
                            onChange={(val) => {
                                setRefundAmount(val);
                                if (val) {
                                    setNumberError(undefined);
                                }
                            }}
                            style={{ width: '120px' }}
                            controls={false}
                            min={0}
                            precision={2}
                            addonBefore="$"
                        />
                        {!selectFullRefund && numberError && <div className={s.error}>{numberError}</div>}
                    </div>
                </div>
                <div
                    className={s.reItem}
                    onClick={() => {
                        setSelectFullRefund(true);
                        setNumberError(undefined);
                    }}
                >
                    <div>
                        <Radio checked={selectFullRefund}>Full refund: ${maxRefundAmount.toFixed(2)}</Radio>
                        {(item?.depositExcludedCents || 0) > 0 && (
                            <div className={s.desc}>The $10 deposit is non-refundable, so it&apos;s not included.</div>
                        )}
                    </div>
                </div>
                <div className={s.label}>Select reason for refund</div>
                {showReasonError && <div className={s.error}>{REQUIRED_FIELD}</div>}
                <Radio.Group
                    className={s.reasonGroup}
                    onChange={(e) => {
                        setShowReasonError(false);
                        setSelectReason(e.target.value);
                    }}
                    value={selectReason}
                    options={REASON_OPTIONS}
                />
                {selectReason === 'Others' && (
                    <div>
                        <div className={s.label}>Reasons for refund</div>
                        <Input.TextArea
                            value={reasonOthers}
                            onChange={(e) => {
                                setReasonOthers(e.target.value);
                                if (e.target.value) {
                                    setShowReasonError(false);
                                }
                            }}
                            rows={5}
                            maxLength={500}
                            showCount
                        />
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default RefundModal;
